import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import api, { formatPrice, formatApiError } from "../lib/api";

const STATUS_COLOR = { paid: "text-emeraldauth", pending: "text-champagne-dark", failed: "text-vermilion", refunded: "text-stonemuted", cancelled: "text-stonemuted" };

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const { wishlist } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (user) setProfile({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/auth/profile", profile); setUser(data); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const TABS = [["orders", "Orders"], ["profile", "Profile"], ["wishlist", "Wishlist"]];

  return (
    <div className="luxy-container py-12 sm:py-16">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-3">My Account</p>
          <h1 className="font-serif text-4xl text-pitch tracking-tight">Hello, {user?.name?.split(" ")[0] || "there"}</h1>
        </div>
        <button onClick={doLogout} data-testid="account-logout" className="text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1 hover:text-vermilion hover:border-vermilion transition-colors">Sign Out</button>
      </div>

      <div className="flex gap-8 border-b border-linen mb-10">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`account-tab-${v}`}
            className={`pb-3 text-xs font-mono uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${tab === v ? "border-pitch text-pitch" : "border-transparent text-stonemuted hover:text-pitch"}`}>{l}</button>
        ))}
      </div>

      {tab === "orders" && (
        orders.length === 0 ? (
          <div className="py-20 text-center" data-testid="account-no-orders">
            <p className="font-serif text-2xl text-pitch">No orders yet</p>
            <p className="text-stonemuted mt-2">When you make a purchase, it will appear here.</p>
            <Link to="/shop" className="inline-block mt-6 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((o) => (
              <div key={o.id} className="border border-linen p-6" data-testid="account-order">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="font-mono text-sm tracking-wider">{o.id}</div>
                    <div className="text-xs text-stonemuted mt-1">{new Date(o.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-widest">
                    <span className={STATUS_COLOR[o.payment_status] || "text-charcoal"}>{o.payment_status}</span>
                    <span className="text-stonemuted">·</span>
                    <span className="text-charcoal">{o.fulfillment_status}</span>
                  </div>
                </div>
                <div className="space-y-1 text-sm text-charcoal/80">
                  {o.items.map((i, idx) => <div key={idx} className="flex justify-between"><span>{i.brand} {i.title}</span><span>{formatPrice(i.price)}</span></div>)}
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t border-linen font-medium">
                  <span>Total</span><span>{formatPrice(o.total)} {o.currency}</span>
                </div>
                {o.tracking_number && <div className="text-xs text-stonemuted mt-3">Tracking: {o.tracking_number}</div>}
              </div>
            ))}
          </div>
        )
      )}

      {tab === "profile" && (
        <form onSubmit={saveProfile} className="max-w-md space-y-4">
          <div>
            <label className="eyebrow block mb-2">Email</label>
            <input value={user?.email || ""} disabled className="w-full border border-linen bg-bone px-4 py-3 text-sm text-stonemuted" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Full Name</label>
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" className="w-full border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-pitch" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Phone</label>
            <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" className="w-full border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-pitch" />
          </div>
          <button type="submit" data-testid="profile-save" className="bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-charcoal transition-colors">Save Changes</button>
        </form>
      )}

      {tab === "wishlist" && (
        <div className="py-8 text-center">
          <p className="text-charcoal/70">You have {wishlist.length} {wishlist.length === 1 ? "piece" : "pieces"} saved.</p>
          <Link to="/wishlist" className="inline-block mt-4 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">View Wishlist</Link>
        </div>
      )}
    </div>
  );
}
